import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import api from '../../services/api';
import useBinaryUrl from '../../utils/useBinaryUrl';
import { BINARY_RESOURCES, hasBinaryField } from '../../utils/binaryFile';
import './training.css';

const DEFAULT_LIMIT = 10;

// O vídeo sai pela rota binary (com token) e chega inteiro como blob, então só é baixado
// quando o usuário pede pra assistir — evita baixar todos os vídeos da página de uma vez.
const TrainingVideo = ({ trainingItem }) => {
  const [isRequested, setIsRequested] = useState(false);
  const { url, hasError } = useBinaryUrl({
    resource: BINARY_RESOURCES.training,
    id: isRequested ? trainingItem?.id : null,
    field: 'video',
  });

  if (!hasBinaryField(trainingItem, 'video')) {
    return <div className="training-video-fallback">Vídeo indisponível</div>;
  }

  if (url) {
    return (
      <video className="training-video" controls autoPlay preload="metadata">
        <source src={url} />
        Seu navegador não suporta a reprodução de vídeos.
      </video>
    );
  }

  if (hasError) {
    return <div className="training-video-fallback">Não foi possível carregar o vídeo.</div>;
  }

  if (isRequested) {
    return <div className="training-video-fallback">Carregando vídeo...</div>;
  }

  return (
    <button
      type="button"
      className="training-video-fallback"
      style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
      onClick={() => setIsRequested(true)}
    >
      ▶ Assistir vídeo
    </button>
  );
};

const getRequestPagination = ({ url, params, fallbackLimit }) => {
  const limitFromParams = Number(params?.limit || params?.page_size);
  const offsetFromParams = Number(params?.offset);

  if (Number.isFinite(limitFromParams) && limitFromParams > 0) {
    return {
      limit: limitFromParams,
      offset: Number.isFinite(offsetFromParams) && offsetFromParams >= 0 ? offsetFromParams : 0,
    };
  }

  if (!url) {
    return { limit: fallbackLimit, offset: 0 };
  }

  try {
    const parsedUrl = new URL(url, api.defaults.baseURL || window.location.origin);
    const limit = Number(parsedUrl.searchParams.get('limit') || parsedUrl.searchParams.get('page_size')) || fallbackLimit;
    const offset = Number(parsedUrl.searchParams.get('offset'));
    const page = Number(parsedUrl.searchParams.get('page'));

    if (Number.isFinite(offset) && offset >= 0) {
      return { limit, offset };
    }

    if (Number.isFinite(page) && page > 0) {
      return { limit, offset: (page - 1) * limit };
    }

    return { limit, offset: 0 };
  } catch {
    return { limit: fallbackLimit, offset: 0 };
  }
};

const formatModule = (moduleName, moduleValue) => {
  if (moduleName !== null && moduleName !== undefined && String(moduleName).trim() !== '') {
    return String(moduleName);
  }

  if (moduleValue === null || moduleValue === undefined || moduleValue === '') return '-';
  if (typeof moduleValue === 'object') {
    return moduleValue?.name || moduleValue?.title || moduleValue?.id || '-';
  }
  return moduleValue;
};

function Training() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    limit: DEFAULT_LIMIT,
    offset: 0,
  });

  const loadTrainings = useCallback(async ({ url = null, params = null } = {}) => {
    setLoading(true);
    setError('');

    try {
      const requestUrl = url || '/users/training/';
      const requestConfig = url ? {} : { params: params || { limit: DEFAULT_LIMIT, offset: 0 } };
      const response = await api.get(requestUrl, requestConfig);
      const payload = response?.data || {};
      const results = Array.isArray(payload) ? payload : payload?.results || [];
      const requestPagination = getRequestPagination({
        url,
        params: requestConfig?.params,
        fallbackLimit: DEFAULT_LIMIT,
      });

      setTrainings(results);
      setPagination({
        count: typeof payload?.count === 'number' ? payload.count : results.length,
        next: payload?.next || null,
        previous: payload?.previous || null,
        limit: requestPagination.limit,
        offset: requestPagination.offset,
      });
    } catch (requestError) {
      console.error('Erro ao buscar treinamentos:', requestError);
      setTrainings([]);
      setError('Não foi possível carregar os treinamentos agora.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrainings({ params: { limit: DEFAULT_LIMIT, offset: 0 } });
  }, [loadTrainings]);

  const currentPage = useMemo(() => {
    const safeLimit = pagination.limit > 0 ? pagination.limit : DEFAULT_LIMIT;
    return Math.floor(pagination.offset / safeLimit) + 1;
  }, [pagination.limit, pagination.offset]);

  const totalPages = useMemo(() => {
    const safeLimit = pagination.limit > 0 ? pagination.limit : DEFAULT_LIMIT;
    return Math.max(1, Math.ceil((pagination.count || 0) / safeLimit));
  }, [pagination.count, pagination.limit]);

  const shouldShowPagination = (pagination.count || trainings.length) > DEFAULT_LIMIT;

  const handlePrevious = () => {
    if (!pagination.previous || loading) return;
    loadTrainings({ url: pagination.previous });
  };

  const handleNext = () => {
    if (!pagination.next || loading) return;
    loadTrainings({ url: pagination.next });
  };

  return (
    <div>
      <Sidebar />
      <Header />

      <div className="container training-container">
        <div className="content training-content">
          <h4 className="h4-title">Treinamentos</h4>
          <p className="training-subtitle">Vídeos de capacitação disponíveis no sistema.</p>

          {loading && (
            <p className="training-feedback">Carregando treinamentos...</p>
          )}

          {!loading && error && (
            <p className="training-feedback training-feedback-error">{error}</p>
          )}

          {!loading && !error && trainings.length === 0 && (
            <p className="training-feedback">Nenhum treinamento disponível.</p>
          )}

          {!loading && !error && trainings.length > 0 && (
            <>
              <div className="training-grid">
                {trainings.map((trainingItem) => {
                  return (
                    <article key={trainingItem?.id} className="training-card">
                      <div className="training-video-wrapper">
                        <TrainingVideo trainingItem={trainingItem} />
                      </div>

                      <div className="training-card-body">
                        <h3>{trainingItem?.title || 'Sem título'}</h3>
                        <p className="training-description">{trainingItem?.description || 'Sem descrição.'}</p>
                        <p className="training-module">
                          <strong>Módulo:</strong> {formatModule(trainingItem?.module_name, trainingItem?.module)}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>

              {shouldShowPagination && (
                <div className="training-pagination">
                  <button type="button" onClick={handlePrevious} disabled={!pagination.previous || loading}>
                    Anterior
                  </button>
                  <span>Página {currentPage} de {totalPages}</span>
                  <button type="button" onClick={handleNext} disabled={!pagination.next || loading}>
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Training;
